import { useState, useEffect, useRef, useCallback } from "react";

interface UseCountdownProps {
  initialTime: number; // in seconds
  onComplete?: () => void;
}

interface UseCountdownReturn {
  timeLeft: number;
  isActive: boolean;
  start: () => void;
  reset: () => void;
  stop: () => void;
}

export const useCountdown = ({
  initialTime,
  onComplete,
}: UseCountdownProps): UseCountdownReturn => {
  const [timeLeft, setTimeLeft] = useState(initialTime);
  const [isActive, setIsActive] = useState(false);
  const intervalRef = useRef<NodeJS.Timeout | null>(null);
  const onCompleteRef = useRef(onComplete);

  // Update the ref when onComplete changes
  useEffect(() => {
    onCompleteRef.current = onComplete;
  }, [onComplete]);

  const stop = useCallback(() => {
    setIsActive(false);
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
  }, []);

  const start = useCallback(() => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
    }
    setTimeLeft(initialTime);
    setIsActive(true);
  }, [initialTime]);

  const reset = useCallback(() => {
    stop();
    setTimeLeft(initialTime);
  }, [initialTime, stop]);

  useEffect(() => {
    if (isActive) {
      intervalRef.current = setInterval(() => {
        setTimeLeft((prevTime) => {
          if (prevTime <= 1) {
            setIsActive(false);
            onCompleteRef.current?.();
            return 0;
          }
          return prevTime - 1;
        });
      }, 1000);
    }

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    };
  }, [isActive]);

  return {
    timeLeft,
    isActive,
    start,
    reset,
    stop,
  };
};
